"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
    DownloadIcon as FiDownload,
    FileSearchIcon as FiSearch,
    FilterIcon as FiFilter,
    Trash2Icon as FiTrash2,
    RefreshCwIcon as FiRefreshCw,
    ChevronLeftIcon as FiChevronLeft,
    ChevronRightIcon as FiChevronRight,
    CheckIcon as FiCheck,
    EyeIcon as FiEye,
    XIcon as FiX,
    Sparkles,
    CreditCardIcon as FiCreditCard,
    Wallet,
} from "lucide-react"
import { getAllPayoutRequests, getAppointments, updatePayoutStatus } from "../utils/api"
import { useQuery } from "@tanstack/react-query"
import moment from "moment"

const MentorsPayoutRequests = () => {
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [payoutRequests, setPayoutRequests] = useState()

    const { data: appointments } = useQuery({
        queryKey: ['transactions'],
        queryFn: getAppointments
    })
    const { data: payouts, isLoading, refetch } = useQuery({
        queryKey: ['payouts'],
        queryFn: getAllPayoutRequests
    })


    const handleStatusChange = async (payoutId, status) => {
        const payload = { status: status }
        try {
            await updatePayoutStatus(payoutId, payload)
            setPayoutRequests((prev) =>
                prev.map((item) =>
                    item._id === payoutId ? { ...item, status } : item
                )
            );
        } catch (error) {
            console.error("Status update failed", error);
        }
    };



    useEffect(() => {
        setPayoutRequests(payouts?.payoutRequests)
    }, [payouts])

    const Detail = ({ label, value }) => (
        <div className="flex flex-col">
            <span className="text-xs text-gray-500">{label}</span>
            <span className="text-sm font-semibold text-gray-900 break-all">
                {value ?? "—"}
            </span>
        </div>
    );
    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
                {/* Background Pattern */}
                <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />
                <div className="relative z-10 max-w-7xl mx-auto">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
                    >
                        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                                    <Wallet className="text-white text-2xl" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                                        Payout Requests
                                    </h1>
                                    <p className="text-gray-600 text-lg">
                                        View and manage mentor payout requests, including approval status and amounts
                                    </p>
                                </div>



                            </div>

                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8 overflow-x-auto"
                    >
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {[
                                        "Requested On",
                                        "Mentor Name",
                                        "Mentor Email",
                                        "Amount",
                                        "Status",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            scope="col"
                                            className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {payoutRequests?.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="px-4 py-12 text-center text-gray-500 text-lg">
                                            No payout requests found
                                        </td>
                                    </tr>
                                ) : (
                                    payoutRequests?.map((row) => {


                                        return (
                                            <tr key={row.bookingId} className="hover:bg-gray-50">
                                                {/* Booked On */}
                                                <td className="px-4 py-3 text-sm text-gray-800">
                                                    {moment(row?.createdAt).format("DD MMM YYYY")}
                                                </td>

                                                {/* Meeting Title */}
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                    {row?.mentorId?.name}
                                                </td>

                                                {/* Meeting Date */}
                                                <td className="px-4 py-3 text-sm text-gray-800">
                                                    {row?.mentorId?.email}
                                                </td>

                                                {/* Time */}
                                                <td className="px-4 py-3 text-sm text-gray-800">
                                                    ₹ {row?.amount?.toLocaleString("en-IN")}
                                                </td>

                                                {/* Status */}
                                                <td className="px-4 py-3">
                                                    <select
                                                        value={row?.status}
                                                        onChange={(e) => handleStatusChange(row._id, e.target.value)}
                                                        className={`  px-3 py-1 rounded-full text-xs font-semibold  border border-gray-300 bg-white  focus:outline-none focus:ring-2 focus:ring-blue-500/30  ${row?.status === "pending" ? "bg-yellow-100 text-yellow-800" : row?.status === "approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} `}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </td>
                                            </tr>

                                        );
                                    })
                                )}
                            </tbody>

                        </table>
                    </motion.div>


                </div>

                {/* Delete Dialog */}



            </div>

            <AnimatePresence>
                {isModalOpen && selectedTransaction && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {/* Modal Card */}
                        <motion.div
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 relative"
                            initial={{ scale: 0.9, y: 40 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 40 }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-900">
                                    Transaction Details
                                </h2>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Content */}
                            <div className="grid grid-cols-2 gap-4 text-sm">

                                {/* Meeting */}
                                <Detail label="Meeting Title" value={selectedTransaction.meeting.title} />
                                <Detail
                                    label="Meeting Date"
                                    value={moment(selectedTransaction.meeting.date).format("DD MMM YYYY")}
                                />
                                <Detail
                                    label="Meeting Time"
                                    value={`${moment(selectedTransaction.meeting.startTime, "HH:mm").format("hh:mm A")} – 
            ${moment(selectedTransaction.meeting.endTime, "HH:mm").format("hh:mm A")}`}
                                />

                                {/* Startup */}
                                <Detail label="Startup Name" value={selectedTransaction.startup.name} />
                                <Detail label="Startup Email" value={selectedTransaction.startup.email} />

                                {/* Mentor */}
                                <Detail label="Mentor Name" value={selectedTransaction.mentor.name} />
                                <Detail label="Mentor Email" value={selectedTransaction.mentor.email} />

                                {/* Booking */}
                                <Detail label="Payment Type" value={selectedTransaction.booking.paymentStatus} />
                                <Detail
                                    label="Amount Paid"
                                    value={`₹${selectedTransaction.booking.amountPaid}`}
                                />

                                <Detail label="Booking Status" value={selectedTransaction.status} />
                                <Detail
                                    label="Booked On"
                                    value={moment(selectedTransaction.booking.appliedAt).format(
                                        "DD MMM YYYY, hh:mm A"
                                    )}
                                />
                            </div>


                            {/* Footer */}
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default MentorsPayoutRequests
